import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/apiService';
import { assessConnection, ConnectionAssessment } from '../utils/connectionCheck';
import { fillCounts, loadCorpusStats, onCorpusStats, getCorpusStatsSync }
  from '../utils/corpusStats';

/**
 * Shown when a Direct Search runs past ~5s. Two jobs:
 *
 *  1. Entertain honestly — a witty animation + rotating status lines that say
 *     "we're thinking hard", because for hybrid search 6-10s is the pipeline
 *     (retrieval + rerank + LLM), not a problem.
 *  2. Blame the network ONLY when a real measurement says so. On mount we
 *     probe the API (utils/connectionCheck) and show the amber wifi banner
 *     just for a 'slow'/'offline' verdict — never on the timer alone.
 *
 * Six CSS-only animation variants; one is picked per search so repeat
 * searchers see variety. Respects prefers-reduced-motion.
 */

// Still rendered through fillCounts(), so a future message can use
// {tech_count} and stay true to the live corpus; none of these need it today.
const MESSAGES: string[] = [
  'Searching the cosmos… ✨',
  'Going beyond the keywords…',
  'Finding the signal in the noise…',
  'Connecting the dots…',
  'Exploring the research universe…',
  'Finding the best matches…',
];

const SLOW_NET_EXTRA = 'Also — your connection is on the slow side, which isn’t helping.';

type Variant = 'atom' | 'radar' | 'flask' | 'scan' | 'comet' | 'gears';
const VARIANTS: Variant[] = ['atom', 'radar', 'flask', 'scan', 'comet', 'gears'];

// Keyframes can't come from the CDN Tailwind build, so the component carries
// its own stylesheet. Class names are st- prefixed to stay out of the way.
const STYLES = `
.st-stage { width: 88px; height: 88px; position: relative; flex-shrink: 0; }
.st-stage svg { width: 100%; height: 100%; display: block; }
@keyframes st-spin { to { transform: rotate(360deg); } }
@keyframes st-spin-rev { to { transform: rotate(-360deg); } }
@keyframes st-pulse { 0%,100% { opacity: .25; } 50% { opacity: 1; } }
@keyframes st-bubble { 0% { transform: translateY(0); opacity: 0; } 15% { opacity: 1; } 100% { transform: translateY(-26px); opacity: 0; } }
@keyframes st-scanline { 0%,100% { transform: translateY(0); } 50% { transform: translateY(34px); } }
@keyframes st-comet { 0% { transform: translate(-30px, 18px); opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { transform: translate(78px, -30px); opacity: 0; } }
@keyframes st-twinkle { 0%,100% { opacity: .15; } 50% { opacity: .9; } }
.st-orbit { transform-origin: 44px 44px; animation: st-spin 2.4s linear infinite; }
.st-orbit2 { transform-origin: 44px 44px; animation: st-spin-rev 3.6s linear infinite; }
.st-sweep { transform-origin: 44px 44px; animation: st-spin 2.2s linear infinite; }
.st-blip { animation: st-pulse 1.6s ease-in-out infinite; }
.st-bub { animation: st-bubble 1.8s ease-in infinite; }
.st-scan { animation: st-scanline 2.2s ease-in-out infinite; }
.st-comet-dot { animation: st-comet 2.6s ease-in-out infinite; }
.st-star { animation: st-twinkle 2s ease-in-out infinite; }
.st-gear { transform-origin: 32px 40px; animation: st-spin 3.2s linear infinite; }
.st-gear2 { transform-origin: 58px 56px; animation: st-spin-rev 2.4s linear infinite; }
.st-fade { transition: opacity .35s ease; }
@media (prefers-reduced-motion: reduce) {
  .st-stage * { animation: none !important; }
}
`;

const Stage: React.FC<{ variant: Variant }> = ({ variant }) => {
  // All variants draw in a 0 0 88 88 viewBox with the brand purple/gray mix.
  const stroke = '#7c6df2';
  const dim = '#c9c3f5';
  switch (variant) {
    case 'atom':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          <circle cx="44" cy="44" r="5" fill={stroke} />
          <ellipse cx="44" cy="44" rx="30" ry="12" fill="none" stroke={dim} strokeWidth="1.5" />
          <ellipse cx="44" cy="44" rx="30" ry="12" fill="none" stroke={dim} strokeWidth="1.5" transform="rotate(60 44 44)" />
          <g className="st-orbit"><circle cx="74" cy="44" r="4" fill={stroke} /></g>
          <g className="st-orbit2"><circle cx="44" cy="14" r="3" fill={stroke} opacity=".7" /></g>
        </svg>
      );
    case 'radar':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          <circle cx="44" cy="44" r="32" fill="none" stroke={dim} strokeWidth="1.5" />
          <circle cx="44" cy="44" r="20" fill="none" stroke={dim} strokeWidth="1" opacity=".6" />
          <circle cx="44" cy="44" r="9" fill="none" stroke={dim} strokeWidth="1" opacity=".4" />
          <g className="st-sweep">
            <path d="M44 44 L44 12 A32 32 0 0 1 60 17 Z" fill={stroke} opacity=".35" />
            <line x1="44" y1="44" x2="44" y2="12" stroke={stroke} strokeWidth="2" />
          </g>
          <circle className="st-blip" cx="58" cy="32" r="3" fill={stroke} />
          <circle className="st-blip" cx="30" cy="56" r="2.5" fill={stroke} style={{ animationDelay: '.6s' }} />
        </svg>
      );
    case 'flask':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          <path d="M36 16 h16 v14 l12 26 a6 6 0 0 1-5.5 8.5 h-29 A6 6 0 0 1 24 56 l12-26 Z"
            fill="none" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
          <path d="M30 52 h28 l4 8 a4 4 0 0 1-3.8 5.5 h-28.4 A4 4 0 0 1 26 60 Z" fill={dim} opacity=".55" />
          <circle className="st-bub" cx="40" cy="58" r="2.5" fill={stroke} />
          <circle className="st-bub" cx="48" cy="60" r="2" fill={stroke} style={{ animationDelay: '.5s' }} />
          <circle className="st-bub" cx="44" cy="56" r="1.6" fill={stroke} style={{ animationDelay: '1s' }} />
        </svg>
      );
    case 'scan':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          <rect x="22" y="18" width="44" height="52" rx="4" fill="none" stroke={dim} strokeWidth="2" />
          {[28, 36, 44, 52, 60].map(y => (
            <line key={y} x1="28" y1={y} x2="60" y2={y} stroke={dim} strokeWidth="2" strokeLinecap="round" />
          ))}
          <g className="st-scan">
            <rect x="20" y="24" width="48" height="6" rx="3" fill={stroke} opacity=".45" />
          </g>
        </svg>
      );
    case 'comet':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          {[[16, 22], [66, 14], [74, 58], [24, 64], [50, 40]].map(([x, y], i) => (
            <circle key={i} className="st-star" cx={x} cy={y} r="1.8" fill={stroke}
              style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
          <g className="st-comet-dot">
            <line x1="8" y1="62" x2="26" y2="50" stroke={dim} strokeWidth="3" strokeLinecap="round" />
            <circle cx="28" cy="49" r="5" fill={stroke} />
          </g>
        </svg>
      );
    case 'gears':
      return (
        <svg viewBox="0 0 88 88" aria-hidden="true">
          <g className="st-gear">
            {[0, 45, 90, 135, 180, 225, 270, 315].map(a => (
              <rect key={a} x="29.5" y="22" width="5" height="8" rx="1.5" fill={stroke}
                transform={`rotate(${a} 32 40)`} />
            ))}
            <circle cx="32" cy="40" r="11" fill="none" stroke={stroke} strokeWidth="3" />
          </g>
          <g className="st-gear2">
            {[0, 60, 120, 180, 240, 300].map(a => (
              <rect key={a} x="55.8" y="42" width="4.5" height="7" rx="1.5" fill={dim}
                transform={`rotate(${a} 58 56)`} />
            ))}
            <circle cx="58" cy="56" r="8.5" fill="none" stroke={dim} strokeWidth="3" />
          </g>
        </svg>
      );
  }
};

interface SearchThinkingProps {
  /** Epoch ms when the search actually started, for the elapsed counter. */
  startedAt: number;
}

const SearchThinking: React.FC<SearchThinkingProps> = ({ startedAt }) => {
  // Stable per-mount so the animation doesn't switch mid-search.
  const variant = useMemo<Variant>(
    () => VARIANTS[Math.floor(Math.random() * VARIANTS.length)], []);
  const [msgIdx, setMsgIdx] = useState(() => Math.floor(Math.random() * MESSAGES.length));
  // Live corpus size for the "{tech_count} technologies" line. Starts from the
  // cached value so the first frame is never blank, then updates if the fetch
  // returns something different.
  const [corpus, setCorpus] = useState(getCorpusStatsSync());
  useEffect(() => {
    loadCorpusStats().then(setCorpus);
    return onCorpusStats(setCorpus);
  }, []);
  const [visible, setVisible] = useState(true);
  const [elapsed, setElapsed] = useState(() => Math.round((Date.now() - startedAt) / 1000));
  const [net, setNet] = useState<ConnectionAssessment | null>(null);
  const probing = useRef(false);

  // Rotate the message with a short fade so it reads as alive, not glitchy.
  useEffect(() => {
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIdx(i => (i + 1) % MESSAGES.length);
        setVisible(true);
      }, 350);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  // One probe per mount — this component only exists while a search is slow.
  useEffect(() => {
    if (probing.current) return;
    probing.current = true;
    assessConnection(apiService.getApiBaseUrl())
      .then(setNet)
      .catch(() => { /* verdict stays null → no network banner */ });
  }, []);

  const showNetBanner = net?.verdict === 'slow' || net?.verdict === 'offline';

  return (
    <div>
      <style>{STYLES}</style>
      {showNetBanner && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12.55a11 11 0 0 1 14 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M8.5 16.05a6 6 0 0 1 7 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 19.5h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M2 8.82a16 16 0 0 1 20 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          </svg>
          {net?.verdict === 'offline'
            ? 'You appear to be offline — check your connection.'
            : `Your connection looks slow right now${net?.rttMs ? ` (~${Math.round(net.rttMs)}ms to our servers)` : ''} — hang tight, still working.`}
        </div>
      )}
      <div className="flex items-center gap-4 p-4 rounded-xl border border-grayscale-200 bg-white max-w-xl">
        <div className="st-stage">
          <Stage variant={variant} />
        </div>
        <div className="min-w-0">
          <div className={`st-fade text-sm text-grayscale-1300 font-medium ${visible ? 'opacity-100' : 'opacity-0'}`}>
            {fillCounts(MESSAGES[msgIdx], corpus)}
            {showNetBanner && net?.verdict === 'slow' ? ` ${SLOW_NET_EXTRA}` : ''}
          </div>
          <div className="text-xs text-grayscale-1100 mt-1">
            {elapsed}s — deep search takes a little longer than keyword match.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchThinking;
